import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Input, FormGroup, Label, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import VideoActions from '~/actions/video_actions';
import VideoStore from '~/stores/video_store';
import AppModal from './app_modal';
import { TeammatePicker } from '~/components/forms/team_member_picker';

const EditVideoModal = ({ videoUUID, containerID, modalClass, dialogClass, onClose, table, query }) => {
  const [loadingVideo, setLoadingVideo] = useState(!!videoUUID);
  const [video, setVideo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const videoStoreListener = VideoStore.addListener(() => {
      const videoState = VideoStore.getState();
      const { video, errors, categories, lastVideoStoreAction } = videoState;

      if (lastVideoStoreAction === 'updateVideoDone') {
        setSubmitting(false);
        if (onClose) onClose();
      } else if (lastVideoStoreAction === 'updateVideoFail') {
        console.error('Failed to update video:', errors);
        setErrors(errors);
        setSubmitting(false);
      } else if (lastVideoStoreAction === 'loadVideoDone') {
        setLoadingVideo(false);
        setVideo(video);
      } else if (lastVideoStoreAction === 'loadCategoriesDone') {
        setCategories(categories);
      }
    });

    if (videoUUID) {
      VideoActions.loadVideo(videoUUID);
    }
    VideoActions.loadCategories();

    return () => {
      videoStoreListener.remove();
    };
  }, [videoUUID, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const validationErrors = validate();

    if (Object.keys(validationErrors).length === 0) {
      VideoActions.updateVideo(video, {
        loadVideos: true,
        searchData: query?.s || {},
        page: query?.page || 1
      }, table);
    } else {
      setErrors(validationErrors);
      setSubmitting(false);
    }
  };

  const setVideoField = (name, val) => {
    setVideo((prevVideo) => ({
      ...prevVideo,
      [name]: val,
    }));
  };

  const validate = () => {
    const validationErrors = {};
    if (!video?.title) {
      validationErrors.title = "Can't be empty";
    }
    setErrors(validationErrors);
    return validationErrors;
  };

  const renderVideoForm = () => {
    if (loadingVideo || !video) {
      return (
        <div className="text-center">
          <FontAwesomeIcon icon="far fa-spinner" pulse size="lg" />
        </div>
      );
    }

    const category_id = video.category_id || video.category?.id || '';

    return (
      <>
        <div className="row">
          <div className="col-3">
            <img
              src={video.thumbnail}
              alt="Thumbnail"
              width='150px'
              className="rounded mt-4 ml-4 mr-3 float-center"
            />
          </div>
          <div className="col-9">
            <div className="row">
              <div className="col-12">
                <Input
                  type="text"
                  value={video.title || ''}
                  className={classNames('mb-2', { 'is-invalid': errors.title })}
                  placeholder="Demo Video"
                  onChange={(e) => setVideoField('title', e.target.value)}
              />
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </div>
              <div className="col-12">
                <Input
                  type="textarea"
                  value={video.description || ''}
                  className="mb-3"
                  placeholder="Add text here for the recipient viewing page ..."
                  onChange={(e) => setVideoField('description', e.target.value)}
                  rows={1}
                />
              </div>
              <div className="col-6">
                <FormGroup className="mb-0">
                  <Label>Category</Label>
                  <Input
                    type="select"
                    value={category_id}
                    className={classNames('mb-2', { 'is-invalid': errors.category_id })}
                    onChange={(e) => setVideoField('category_id', e.target.value || null)}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Input>
                  {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
                </FormGroup>
              </div>
              <div className="col-6">
                <FormGroup className="mb-0">
                  <Label>Owner</Label>
                  <TeammatePicker
                    placeholder="Select Owner"
                    value={video.owner_id || ''}
                    onChange={(opt) => setVideoField('owner_id', opt?.value || null)}
                    className={classNames({ 'is-invalid': errors.owner })}
                    apiResource={'/v1/videos/owner_options'}
                    clearable
                  />
                  {errors.owner && <div className="invalid-feedback">{errors.owner}</div>}
                </FormGroup>
              </div>
              <div className="col-12 mt-2">
                <button
                  type="submit"
                  className="btn btn-outline-success mr-2"
                  disabled={submitting}
                >
                  <FontAwesomeIcon icon="far fa-check" className="mr-2" />
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon="spinner" pulse className="mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={onClose}
                >
                  <FontAwesomeIcon icon="far fa-times" className="mr-2" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <AppModal
      containerID={containerID}
      modalClass={`${modalClass} edit-video-modal`}
      dialogClass={dialogClass}
    >
      <div className="modal-header">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon="far fa-video-plus" className="mr-2" />
          <h5 className="modal-title mb-0">Edit video</h5>
        </div>
        <button type="button" className="close" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {renderVideoForm()}
        </div>
      </form>
    </AppModal>
  );
};

EditVideoModal.defaultProps = {
  modalClass:  '',
  dialogClass: '',
};

EditVideoModal.propTypes = {
  videoUUID:     PropTypes.string.isRequired,
  containerID: PropTypes.string.isRequired,
  modalClass:  PropTypes.string,
  dialogClass: PropTypes.string,
  onClose:     PropTypes.func.isRequired,
  table:       PropTypes.object,
  query:       PropTypes.object,
};

export default EditVideoModal;
